import { useState, useEffect, useCallback, useRef } from 'react';
import WebSocketService from '../services/websocket';

export const useMessages = (contatoAtivo) => {
  const [mensagens, setMensagens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [temMais, setTemMais] = useState(true);
  const [novasMensagens, setNovasMensagens] = useState(0);
  const [mostrarNotificacao, setMostrarNotificacao] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const loadStartRef = useRef(null);
  const mensagensMapRef = useRef(new Map());
  const nextPageRef = useRef(1);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const adicionarMensagem = useCallback((mensagem) => {
    const key = mensagem.id || `${mensagem.create_at}-${mensagem.conteudo || mensagem.mensagem_recebida || mensagem.resposta_enviada}`;
    
    if (mensagensMapRef.current.has(key)) {
      return false; // Duplicata
    }
    
    mensagensMapRef.current.set(key, mensagem);
    setMensagens(prev => [...prev, mensagem]);
    return true;
  }, []);

  const carregarMensagens = useCallback((page = 1, limit = 15) => {
    if (!contatoAtivo || loadingRef.current) return false;
    
    const tentarCarregar = (tentativas = 0) => {
      if (WebSocketService.isAtendimentoConnected) {
        loadingRef.current = true;
        setCarregando(true);
        loadStartRef.current = Date.now();
        console.log(`📜 [useMessages] Requisitando page=${page} limit=${limit} tel=${contatoAtivo.telefone?.substring(0, 10)}...`);
        WebSocketService.carregarMensagens({ 
          telefone: contatoAtivo.telefone, page, limit,
          instanciaId: contatoAtivo.instancia_id
        });
      } else if (tentativas < 5) {
        setTimeout(() => tentarCarregar(tentativas + 1), 500);
      } else {
        loadingRef.current = false;
        setCarregando(false);
      }
    };
    
    tentarCarregar();
    return true;
  }, [contatoAtivo]);

  const carregarMaisMensagens = useCallback(() => {
    if (!hasMoreRef.current || loadingRef.current) return false;
    return carregarMensagens(nextPageRef.current, 15);
  }, [carregarMensagens]);

  const enviarMensagem = useCallback((texto, onSent, instanciaId) => {
    if (!texto.trim() || !contatoAtivo || !WebSocketService.isAtendimentoConnected) return;

    // Pausar IA ANTES de enviar mensagem
    console.log('📵 Enviando mensagem e pausando IA para:', contatoAtivo.telefone);
    WebSocketService.pausarIA({ telefone: contatoAtivo.telefone });

    // Adicionar mensagem temporária imediatamente
    const tempId = `temp-${Date.now()}`;
    const mensagemTemp = {
      id: tempId,
      telefone: contatoAtivo.telefone,
      direcao: 'enviada',
      conteudo: texto,
      mensagem_recebida: null,
      resposta_enviada: texto,
      respondida: true,
      respondida_por_humano: true,
      create_at: new Date().toISOString(),
      nome_contato: contatoAtivo.nome_contato,
      status_mensagem: 'sent'
    };
    
    const key = mensagemTemp.id;
    mensagensMapRef.current.set(key, mensagemTemp);
    setMensagens(prev => [...prev, mensagemTemp]);

    // Callback para scroll
    if (onSent) setTimeout(onSent, 100);

    // Listener temporário pra capturar erro deste envio
    const erroCallback = () => {
      setMensagens(prev => prev.map(m => m.id === tempId ? { ...m, status_mensagem: 'failed' } : m));
      WebSocketService._erroCallbacks?.delete(erroCallback);
    };
    if (!WebSocketService._erroCallbacks) WebSocketService._erroCallbacks = new Set();
    WebSocketService._erroCallbacks.add(erroCallback);
    setTimeout(() => WebSocketService._erroCallbacks?.delete(erroCallback), 10000);

    WebSocketService.enviarMensagemDireta({
      telefone: contatoAtivo.telefone,
      mensagem: texto,
      instanciaId: instanciaId,
    });
  }, [contatoAtivo]);

  // Listener para mensagens carregadas
  useEffect(() => {
    const handleMensagensCarregadas = (data) => {
      if (data.success) {
        const { mensagens: novasMensagens, hasMore, page } = data;
        console.log(`📜 [useMessages] Recebido page=${page}, ${novasMensagens?.length || 0} msgs, hasMore=${hasMore}`);
        
        if (page === 1) {
          mensagensMapRef.current.clear();
          setMensagens(novasMensagens || []);
          novasMensagens?.forEach(msg => {
            const key = msg.id || `${msg.create_at}-${msg.conteudo || msg.mensagem_recebida || msg.resposta_enviada}`;
            mensagensMapRef.current.set(key, msg);
          });
          
          // Scroll será feito pelo MessageList quando mostrarMensagens virar true
        } else {
          const mensagensValidas = (novasMensagens || []).filter(msg => {
            const key = msg.id || `${msg.create_at}-${msg.conteudo || msg.mensagem_recebida || msg.resposta_enviada}`;
            if (!mensagensMapRef.current.has(key)) {
              mensagensMapRef.current.set(key, msg);
              return true;
            }
            return false;
          });
          console.log(`📜 [useMessages] Page ${page}: ${novasMensagens?.length} recebidas, ${mensagensValidas.length} novas (${(novasMensagens?.length || 0) - mensagensValidas.length} duplicatas)`);
          
          setMensagens(prev => [...mensagensValidas, ...prev]);
        }
        
        hasMoreRef.current = !!hasMore;
        nextPageRef.current = page + 1;
        setTemMais(!!hasMore);
      }
      loadingRef.current = false;
      setCarregando(false);
    };

    if (WebSocketService.isAtendimentoConnected && WebSocketService.atendimentoSocket) {
      WebSocketService.atendimentoSocket.on('mensagens_carregadas', handleMensagensCarregadas);
      
      return () => {
        WebSocketService.atendimentoSocket?.off('mensagens_carregadas', handleMensagensCarregadas);
      };
    }
  }, [WebSocketService.isAtendimentoConnected]);

  // Ref para contatoAtivo (evita re-registrar listener a cada mudança)
  const contatoAtivoRef = useRef(contatoAtivo);
  useEffect(() => { contatoAtivoRef.current = contatoAtivo; }, [contatoAtivo]);

  // Listener para novas mensagens em tempo real (registra UMA vez via _novaMensagemCallbacks)
  useEffect(() => {
    const handleNovaMensagem = (conversa) => {
      const contato = contatoAtivoRef.current;
      
      if (contato && conversa.telefone === contato.telefone && conversa.instancia_id === contato.instancia_id) {
        const key = conversa.id;
        
        // Limpar temporárias do map
        if ((conversa.respondida_por_humano && conversa.resposta_enviada) || (conversa.respondida_por_humano && conversa.direcao === 'enviada')) {
          for (const [mapKey] of mensagensMapRef.current) {
            if (mapKey.startsWith('temp-')) {
              mensagensMapRef.current.delete(mapKey);
            }
          }
        }
        
        mensagensMapRef.current.set(key, conversa);
        
        setMensagens(prev => {
          // Remover temporárias se é mensagem de humano
          let updated = prev;
          if ((conversa.respondida_por_humano && conversa.resposta_enviada) || (conversa.respondida_por_humano && conversa.direcao === 'enviada')) {
            updated = updated.filter(msg => !msg.id?.startsWith('temp-'));
          }
          
          // Se já existe, atualizar
          const exists = updated.some(msg => msg.id === key);
          if (exists) {
            return updated.map(msg => msg.id === key ? conversa : msg);
          }
          // Senão, adicionar
          return [...updated, conversa];
        });
      }
    };

    WebSocketService._novaMensagemCallbacks.add(handleNovaMensagem);
    
    return () => {
      WebSocketService._novaMensagemCallbacks.delete(handleNovaMensagem);
    };
  }, []);

  // Listener para atualização de status
  useEffect(() => {
    const handleStatusAtualizado = ({ telefone, status, instanciaId }) => {
      const contato = contatoAtivoRef.current;
      if (contato?.telefone === telefone && contato?.instancia_id === instanciaId) {
        const statusPriority = { sent: 1, delivered: 2, read: 3, failed: 0 };
        
        setMensagens(prev => prev.map(msg => {
          if (!msg.resposta_enviada && msg.direcao !== 'enviada') return msg;
          const currentPriority = statusPriority[msg.status_mensagem] || 0;
          const newPriority = statusPriority[status] || 0;
          return newPriority >= currentPriority ? { ...msg, status_mensagem: status } : msg;
        }));
      }
    };
    
    WebSocketService._statusCallbacks.add(handleStatusAtualizado);
    return () => {
      WebSocketService._statusCallbacks.delete(handleStatusAtualizado);
    };
  }, []);

  // Reset ao trocar contato
  useEffect(() => {
    loadingRef.current = false;
    nextPageRef.current = 1;
    hasMoreRef.current = true;
    if (contatoAtivo) {
      mensagensMapRef.current.clear();
      setMensagens([]);
      setTemMais(true);
      setNovasMensagens(0);
      setMostrarNotificacao(false);
      carregarMensagens(1);
    } else {
      setMensagens([]);
      setTemMais(true);
      setNovasMensagens(0);
      setMostrarNotificacao(false);
    }
  }, [contatoAtivo, carregarMensagens]);

  return {
    mensagens,
    carregando,
    temMais,
    novasMensagens,
    mostrarNotificacao,
    carregarMensagens,
    carregarMaisMensagens,
    enviarMensagem,
    setNovasMensagens,
    setMostrarNotificacao,
    forceUpdate
  };
};